import React from 'react';
import axios from 'axios';
import Group from '../Group';
import Project from '../Project';
import PropTypes from 'prop-types';

const CancelToken = axios.CancelToken;

export default class NameAudit extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            running: false,
            subgroupsToBeShown: [],
            projectsToBeShown: [],
            errorMessage: ''
        };

        this.ajaxStack = 0;
        this.axiosCancelFunctions = [];
        this.urlTypeToGO = 'main_dir';
    }

    componentDidMount() {
        window.addEventListener('beforeunload', this.cancelReport);
        window.addEventListener('turbolinks:visit', this.cancelReport);
    }

    componentWillUnmount() {
        this.cancelReport();
    }

    cancelXhrRequests = () => {
        for (const cancelFunction of this.axiosCancelFunctions) {
            cancelFunction();
        }

        this.ajaxStack = 0; // Reset the stack after canceling
        this.axiosCancelFunctions = []; // Clear the cancel functions after canceling
    };

    runReport = () => {
        this.setState({
            running: true,
            subgroupsToBeShown: [],
            projectsToBeShown: [],
            errorMessage: ''
        }, () => this.fetchGroup(this.props.group));
    };

    cancelReport = (errorMessage = '') => {
        this.setState({running: false, errorMessage}, this.cancelXhrRequests);
    };

    decrementAjaxStack = () => {
        if (this.ajaxStack > 0) {
            this.ajaxStack--;
        }

        if (this.ajaxStack === 0) {
            this.setState({running: false});
        }
    };

    fetchGroup(id) {
        if (this.state.running) {
            this.ajaxStack++;
            axios.get(`/api/gitlab/groups/${id}`, {
                cancelToken: new CancelToken(token => this.axiosCancelFunctions.push(token))
            }).then(response => {
                this.fetchSubgroups(response.data.id);
                this.fetchProjects(response.data.id);
                this.updateShownSubgroups(response.data);
            }).catch(error => {
                if (this.state.running) {
                    const errorMessage = error.response?.data?.error || error.message;
                    this.cancelReport(errorMessage);
                }
            }).finally(this.decrementAjaxStack);
        }
    }

    fetchSubgroups(groupId) {
        if (this.state.running) {
            this.ajaxStack++;
            axios.get(`/api/gitlab/groups/${groupId}/subgroups`, {
                cancelToken: new CancelToken(token => this.axiosCancelFunctions.push(token))
            }).then(async response => {
                for (const group of response.data) {
                    await new Promise(res => setTimeout(res, 500)); // eslint-disable-line no-await-in-loop
                    this.fetchSubgroups(group.id);
                    await new Promise(res => setTimeout(res, 500)); // eslint-disable-line no-await-in-loop
                    this.fetchProjects(group.id);
                    await new Promise(res => setTimeout(res, 500)); // eslint-disable-line no-await-in-loop
                    this.updateShownSubgroups(group);
                }
            })
                .catch(error => {
                    if (this.state.running) {
                        const errorMessage = error.response?.data?.error || error.message;
                        this.cancelReport(errorMessage);
                    }
                })
                .finally(this.decrementAjaxStack);
        }
    }

    fetchProjects(groupId) {
        if (this.state.running) {
            this.ajaxStack++;
            axios.get(`/api/gitlab/groups/${groupId}/projects`, {
                cancelToken: new CancelToken(token => this.axiosCancelFunctions.push(token))
            }).then(async response => {
                for (const project of response.data) {
                    await new Promise(res => setTimeout(res, 500)); // eslint-disable-line no-await-in-loop
                    this.updateShownProjects(project);
                }
            })
                .catch(error => {
                    if (this.state.running) {
                        const errorMessage = error.response?.data?.error || error.message;
                        this.cancelReport(errorMessage);
                    }
                })
                .finally(this.decrementAjaxStack);
        }
    }

    updateShownSubgroups(group) {
        if (this.state.running) {
            const subGroupsToBeShown = this.state.subgroupsToBeShown.concat(group);
            const sorted = subGroupsToBeShown.sort((a, b) => a.name.localeCompare(b.name));
            this.setState({subgroupsToBeShown: sorted});
        }
    }

    updateShownProjects(project) {
        if (this.state.running) {
            const projectsToBeShown = this.state.projectsToBeShown.concat(project);
            const sorted = projectsToBeShown.sort((a, b) => a.name.localeCompare(b.name));
            this.setState({projectsToBeShown: sorted});
        }
    }

    render() {
        return (
            <>
                {this.state.errorMessage &&
                    <div className='callout alert'>
                        {this.state.errorMessage}
                    </div>
                }
                {this.state.running && <div className='primary callout'>
                    <div className='grid-x grid-margin-x align-middle'>
                        <div className='shrink cell'>
                            <i className='fa fa-spinner fa-pulse'/> Running report...
                        </div>
                        <div className='auto cell'>
                            <button
                                className='button no-margin alert'
                                onClick={() => this.cancelReport()}
                                type='button'>
                                Cancel
                            </button>
                        </div>
                        <div className='shrink cell'>
                            API call stack: {this.ajaxStack}
                        </div>
                    </div>
                </div>}
                {!this.state.running && <div className='text-center'>
                    <a className='primary button' onClick={this.runReport}>Run Report</a>
                </div>}
                {(this.state.subgroupsToBeShown.length > 0 || this.state.projectsToBeShown.length > 0) &&
                    <div className='grid-x grid-margin-x small-up-1 medium-up-2'>
                        <div className='cell'>
                            <span className='lead'>
                                Subgroups ({this.state.subgroupsToBeShown.length})
                            </span>
                            <hr/>
                            {!this.state.ajaxStack && !this.state.subgroupsToBeShown.length &&
                                <div className='primary callout'>
                                    There are no groups to show.
                                </div>}
                            {this.state.subgroupsToBeShown.map(group => <div className='tight' key={group.id}>
                                <Group
                                    group={group}
                                    key={group.id}
                                    token={this.props.token}
                                    urlTypeToGo={this.urlTypeToGO}
                                />
                            </div>)}
                        </div>
                        <div className='cell'>
                            <span className='lead'>
                                Projects ({this.state.projectsToBeShown.length})
                            </span>
                            <hr/>
                            {!this.state.ajaxStack && !this.state.projectsToBeShown.length &&
                                <div className='primary callout'>
                                    There are no projects to be shown.
                                </div>}
                            {this.state.projectsToBeShown.map(project => <div className='tight' key={project.id}>
                                <Project
                                    key={project.id}
                                    project={project}
                                    token={this.props.token}
                                    urlTypeToGo={this.urlTypeToGO}
                                />
                            </div>)}
                        </div>
                    </div>}
            </>
        );
    }
}

NameAudit.propTypes = {
    group: PropTypes.number.isRequired,
    token: PropTypes.string.isRequired
};